import { Grid, Typography } from '@rbx/ui';
import React, { FC, useEffect, useState } from 'react';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { useLocalization, useTranslation } from '@rbx/intl';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { ConnectionState, useProvidedConversationReducer } from '@rbx/conv-ai-provider';
import getShuffledExamplePrompts from '../utils/getShuffledExamplePrompts';
import AssistantIcon from './AssistantIcon';
import ConnectionStateAlert from './ConnectionStateAlert';
import ExamplePrompt, { ExamplePromptCategory, ExamplePromptData } from './ExamplePrompt';
import useNullStateStyles from './NullState.styles';
import NullStateIntro from './NullStateIntro';

export type ExamplePromptsProps = {
  onSendExamplePrompt: (prompt: string, category: ExamplePromptCategory) => void;
};

const ExamplePrompts: FC<React.PropsWithChildren<ExamplePromptsProps>> = ({
  onSendExamplePrompt,
}) => {
  const { locale } = useLocalization();
  const { translate } = useTranslation();
  const [examplePrompts, setExamplePrompts] = useState<ExamplePromptData[]>([]);
  const {
    state: { connectionState },
  } = useProvidedConversationReducer();
  const {
    classes: { container, header, headerIcon, examplesWrapper, examplesIntro },
  } = useNullStateStyles();
  useEffect(() => {
    const samples = getShuffledExamplePrompts(
      {
        [ExamplePromptCategory.Build]: translate('ExamplePrompts.Build'),
        [ExamplePromptCategory.Grow]: translate('ExamplePrompts.Grow'),
        [ExamplePromptCategory.Monetize]: translate('ExamplePrompts.Monetize'),
      },
      {
        [ExamplePromptCategory.Build]: translate('ExamplePromptsCategoryHeader.Build'),
        [ExamplePromptCategory.Grow]: translate('ExamplePromptsCategoryHeader.Grow'),
        [ExamplePromptCategory.Monetize]: translate('ExamplePromptsCategoryHeader.Monetize'),
      },
    );
    setExamplePrompts(samples);
  }, [translate]);
  useEffect(() => {
    unifiedLoggerClient.logImpressionEvent({
      eventName: DocsAssistantEvent.NullState,
      parameters: {
        locale: locale ? String(locale) : '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // TODO (@ahua, CRK-2919): UI needs translation strings
  return (
    <Grid flex={1} item XSmall={12} className={container}>
      <Grid item>
        <Typography
          aria-roledescription={translate('Label.Title')}
          className={header}
          variant='h1'
          component='h1'>
          <AssistantIcon size={36} className={headerIcon} /> {translate('Button.Assistant')}
        </Typography>
        <NullStateIntro />
      </Grid>
      <Grid item className={examplesWrapper}>
        <Typography variant='body1' className={examplesIntro} component='div' marginBottom={1}>
          {translate('NullState.ExamplePromptsLabel')}:
        </Typography>
      </Grid>
      <Grid item XSmall={12}>
        <Grid container spacing={2} alignItems='stretch'>
          {examplePrompts.map((props: ExamplePromptData) => (
            <ExamplePrompt key={props.prompt} {...props} onClick={onSendExamplePrompt} />
          ))}
        </Grid>
      </Grid>
      {connectionState !== ConnectionState.Connected && (
        <ConnectionStateAlert connectionState={connectionState} />
      )}
    </Grid>
  );
};

export default ExamplePrompts;
